class BodyStandProductForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return
    this.initialized = true
    this.isInitialLoad = true
    this.variants = this.readVariants()
    this.formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      trailingZeroDisplay: 'stripIfInteger',
    })
    this.setupEvents()
    this.update({ syncMedia: true })
  }

  readVariants() {
    const variantsData = this.querySelector('#product-variants-data')
    if (!variantsData) return []
    try {
      const variants = JSON.parse(variantsData.textContent || '[]')
      return Array.isArray(variants) ? variants : []
    } catch (error) {
      console.error('[body-stand-product-form] Error parsing product variants:', error)
      return []
    }
  }

  setupEvents() {
    this.querySelectorAll('[data-stand-option-input]').forEach((input) => {
      input.addEventListener('change', () => this.update({ syncMedia: true, syncUrl: true }))
    })

    this.setupAddToCart()
  }

  setupAddToCart() {
    // Use document-level delegation so sticky nav buttons (rendered outside this
    // custom element in #pdp-sticky-nav, #pdp-sticky-nav-overlay, and #pdp-mobile-cta)
    // are also handled.
    this.onDocumentAddToCartClick =
      this.onDocumentAddToCartClick ||
      ((e) => {
        if (!(e.target instanceof Element)) return
        const button = e.target.closest('[data-add-to-cart]')
        if (!button) return
        const variant = this.getSelectedVariant()
        if (!variant || !variant.available) return
        button.dataset.loading = ''
        this.addVariantToCart(variant.id, button)
      })
    document.addEventListener('click', this.onDocumentAddToCartClick)
  }

  disconnectedCallback() {
    if (this.onDocumentAddToCartClick) {
      document.removeEventListener('click', this.onDocumentAddToCartClick)
    }
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('[data-stand-option-input]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value)
  }

  getSelectedVariant() {
    const selectedOptions = this.getSelectedOptions()
    if (!selectedOptions.length) return this.variants[0] || null
    return (
      this.variants.find((variant) => this.variantMatchesOptions(variant, selectedOptions)) ||
      this.variants.find((variant) => variant.available) ||
      this.variants[0] ||
      null
    )
  }

  variantMatchesOptions(variant, options) {
    const variantOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean)
    return options.every((option, index) => variantOptions[index] === option)
  }

  findVariantForOption(position, value) {
    const selectedOptions = this.getSelectedOptions()
    selectedOptions[position - 1] = value
    return this.variants.find((variant) => this.variantMatchesOptions(variant, selectedOptions))
  }

  update({ syncMedia: doSyncMedia = false, syncUrl = false } = {}) {
    this.updateOptionAvailability()
    const variant = this.getSelectedVariant()
    if (!variant) return

    this.setAttribute('variant-id', String(variant.id))
    const idInput = this.querySelector('input[name="id"]')
    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id)

    this.updatePrice(variant)
    this.updateSelectedLabels()
    this.updateStylePriceLabels()
    this.syncColorImages()
    this.updateStyleDescription()
    this.updateAddToCart(variant)
    if (syncUrl) {
      this.syncUrl(variant)
    }
    if (doSyncMedia) {
      const placement = this.isInitialLoad ? 'after-video' : 'first'
      this.isInitialLoad = false
      this.syncMedia(variant, { placement })
    }
  }

  updateOptionAvailability() {
    this.querySelectorAll('[data-stand-option-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return
      const position = Number(input.dataset.optionPosition)
      const candidate = this.findVariantForOption(position, input.value)
      input.disabled = !candidate || candidate.available === false
    })

    const checked = this.querySelector('[data-stand-option-input]:checked:disabled')
    if (checked instanceof HTMLInputElement) {
      const replacement = this.querySelector(`[name="${CSS.escape(checked.name)}"]:not(:disabled)`)
      if (replacement instanceof HTMLInputElement) replacement.checked = true
    }
  }

  updatePrice(variant) {
    const price = Number(variant.price) || 0
    const compareAt = Number(variant.compare_at_price) || 0
    const hasCompareAt = compareAt > price

    document.querySelectorAll('[data-main-product-price]').forEach((el) => {
      el.textContent = this.formatter.format(price / 100)
    })

    document.querySelectorAll('[data-main-product-compare-at-price]').forEach((el) => {
      if (hasCompareAt) {
        el.textContent = this.formatter.format(compareAt / 100)
        el.classList.remove('hidden')
      } else {
        el.classList.add('hidden')
      }
    })

    document.querySelectorAll('[data-promo-text]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return
      if (hasCompareAt) {
        el.textContent = `${this.formatter.format((compareAt - price) / 100)} savings`
        el.classList.remove('hidden')
      } else {
        el.classList.add('hidden')
      }
    })
  }

  updateSelectedLabels() {
    this.querySelectorAll('[data-selected-option-label][data-option-position]').forEach((label) => {
      const position = label.getAttribute('data-option-position')
      const input = this.querySelector(`[data-stand-option-input][data-option-position="${position}"]:checked`)
      if (!(input instanceof HTMLInputElement)) return
      label.textContent = input.value
      const compatibilityText = this.getCompatibilityText(input.value)
      if (compatibilityText) {
        label.insertAdjacentHTML('beforeend', ` <span class="font-normal">| ${compatibilityText}</span>`)
      }
    })
  }

  getCompatibilityText(color) {
    const styleInput = this.querySelector('[data-stand-style-input]:checked')
    const standKey = styleInput instanceof HTMLInputElement ? styleInput.dataset.standKey || styleInput.value : ''
    if (/universal|motorized/i.test(standKey)) return 'Apex Pro 1500 & Apex Elite 2160 Compatible'
    if (color.toLowerCase() === 'black') return 'Apex Elite 2160 Compatible'
    if (color.toLowerCase() === 'white') return 'Apex Pro 1500 Compatible'
    return ''
  }

  updateStylePriceLabels() {
    this.querySelectorAll('[data-stand-style-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return
      const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`)
      const priceEl = label?.querySelector('[data-stand-option-price]')
      if (!priceEl) return
      const position = Number(input.dataset.optionPosition)
      const variant = this.findVariantForOption(position, input.value)
      if (variant) priceEl.textContent = this.formatter.format((Number(variant.price) || 0) / 100)
    })
  }

  syncColorImages() {
    const colorInput = Array.from(this.querySelectorAll('[data-stand-option-input]:checked')).find((input) => {
      return input instanceof HTMLInputElement && /black|white/i.test(input.value)
    })
    const color = colorInput instanceof HTMLInputElement ? colorInput.value.toLowerCase() : ''
    if (color !== 'black' && color !== 'white') return

    this.querySelectorAll('img[data-stand-option-image]').forEach((img) => {
      img.classList.toggle('hidden', img.getAttribute('data-stand-option-image') !== color)
    })
  }

  updateStyleDescription() {
    const styleInput = this.querySelector('[data-stand-style-input]:checked')
    const standKey = styleInput instanceof HTMLInputElement ? styleInput.dataset.standKey : ''
    this.querySelectorAll('[data-stand-style-description]').forEach((description) => {
      description.classList.toggle('hidden', description.getAttribute('data-stand-style-description') !== standKey)
    })
  }

  updateAddToCart(variant) {
    const forceOOS = this.hasAttribute('data-force-out-of-stock')
    this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      const text = button.querySelector('[data-text]')
      const unavailable = forceOOS || variant.available === false
      button.toggleAttribute('disabled', unavailable)
      button.classList.toggle('pointer-events-none', unavailable)
      button.classList.toggle('cursor-not-allowed', unavailable)
      if (text) text.textContent = unavailable ? 'OUT OF STOCK' : 'Add to Cart'
    })
  }

  addVariantToCart(variantId, button) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ quantity: 1, id: Number(variantId) }] }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json()
          return alert(error.description || error.message)
        }
        window.HybridCart.open()
      })
      .catch((error) => {
        console.error('[body-stand-product-form] Error adding to cart:', error)
      })
      .finally(() => {
        delete button.dataset.loading
      })
  }

  syncUrl(variant) {
    if (typeof window === 'undefined' || !window.history?.replaceState) return
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.get('variant') === String(variant.id)) return
      url.searchParams.set('variant', String(variant.id))
      window.history.replaceState({}, '', url.toString())
    } catch (_) {}
  }

  getVariantMediaIds(variant) {
    return [variant.featured_media?.id, variant.featured_image?.id].filter(Boolean).map(String)
  }

  findSlideByMediaIds(slides, mediaIds) {
    const desired = new Set(mediaIds)
    return slides.find(
      (slide) =>
        desired.has(slide.getAttribute?.('data-media-id')) || desired.has(slide.getAttribute?.('data-image-id'))
    )
  }

  rememberOriginalMediaOrder(mediaRoot) {
    if (mediaRoot._standProductOriginalMainSlides) return
    const mainList = mediaRoot.querySelector('[data-product-media-v2-main] .splide__list')
    const thumbsList = mediaRoot.querySelector('[data-product-media-v2-thumbs-list]')
    mediaRoot._standProductOriginalMainSlides = mainList ? Array.from(mainList.children) : []
    mediaRoot._standProductOriginalThumbSlides = thumbsList ? Array.from(thumbsList.children) : []
  }

  reorderMediaList(list, originalSlides, selectedSlide, placement) {
    if (!list) return []
    const seeItSlide =
      placement === 'after-video'
        ? originalSlides.find((slide) => slide.hasAttribute('data-see-it-in-action-main-slide'))
        : null
    const leadingSlides = [seeItSlide, selectedSlide].filter(Boolean)
    if (!leadingSlides.length) return []

    const orderedSlides = [...leadingSlides, ...originalSlides.filter((slide) => !leadingSlides.includes(slide))]
    list.replaceChildren(...orderedSlides)
    return orderedSlides
  }

  reorderThumbList(mediaRoot, mainOrderedSlides) {
    const thumbsList = mediaRoot.querySelector('[data-product-media-v2-thumbs-list]')
    const originalThumbSlides = mediaRoot._standProductOriginalThumbSlides || []
    if (!thumbsList || !originalThumbSlides.length || !mainOrderedSlides.length) return

    const orderedThumbs = mainOrderedSlides
      .map((slide) => slide.getAttribute('data-body-slide-index'))
      .map((slideIndex) =>
        originalThumbSlides.find((thumb) => thumb.getAttribute('data-body-slide-index') === slideIndex)
      )
      .filter(Boolean)

    orderedThumbs.forEach((thumb, index) => {
      const button = thumb.querySelector('[data-product-media-v2-thumb]')
      if (button) button.dataset.slideIndex = String(index)
    })
    if (orderedThumbs.length === mainOrderedSlides.length) {
      thumbsList.replaceChildren(...orderedThumbs)
    }
  }

  updateFloatingImage(selectedSlide) {
    const floatImg = document.querySelector(
      `[data-floating-product-image][data-floating-product-image-section="${CSS.escape(this.dataset.sectionId)}"] img`
    )
    const selectedImg = selectedSlide?.querySelector('img')
    const src = selectedImg?.currentSrc || selectedImg?.src
    if (!floatImg || !src) return
    const url = new URL(src, window.location.href)
    url.searchParams.set('width', '300')
    url.searchParams.set('height', '300')
    url.searchParams.set('crop', 'center')
    floatImg.removeAttribute('srcset')
    floatImg.width = 300
    floatImg.height = 300
    floatImg.src = url.toString()
  }

  syncMedia(variant, { placement = 'first', attempt = 0 } = {}) {
    const sectionId = this.dataset.sectionId
    if (!sectionId) return

    const mediaRoot = document.querySelector(`[data-product-media-v2="${CSS.escape(sectionId)}"]`)
    const splide = mediaRoot?._bodyProductMediaSplide
    if (!mediaRoot || !splide) {
      if (attempt < 60) {
        requestAnimationFrame(() => this.syncMedia(variant, { placement, attempt: attempt + 1 }))
      }
      return
    }

    this.rememberOriginalMediaOrder(mediaRoot)
    const mainList = mediaRoot.querySelector('[data-product-media-v2-main] .splide__list')
    const originalMainSlides = mediaRoot._standProductOriginalMainSlides || []

    const mediaIds = this.getVariantMediaIds(variant)
    const selectedSlide = mediaIds.length ? this.findSlideByMediaIds(originalMainSlides, mediaIds) : null

    const orderedSlides = this.reorderMediaList(mainList, originalMainSlides, selectedSlide, placement)
    if (orderedSlides.length) {
      this.reorderThumbList(mediaRoot, orderedSlides)
      if (selectedSlide) {
        this.updateFloatingImage(selectedSlide)
      }

      try {
        splide.refresh()
        mediaRoot._bodyProductThumbSplide?.refresh?.()
        mediaRoot._applyProductGalleryRoles?.()
      } catch (_) {}

      try {
        splide.go(0)
      } catch (_) {}
    }
  }
}

if (!customElements.get('body-stand-product-form')) {
  customElements.define('body-stand-product-form', BodyStandProductForm)
}
